import { API_BASE, createFetchOptions } from './config'

/**
 * Admin API service - all admin endpoints with automatic cookie authentication
 */
export const adminApi = {
  // Models
  async getModels(filters?: Record<string, any>) {
    const params = new URLSearchParams()
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value))
        }
      })
    }

    const response = await fetch(
      `${API_BASE}/api/v2/admin/models?${params}`,
      createFetchOptions()
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch models: ${response.statusText}`)
    }

    return response.json()
  },

  async getModel(id: string) {
    const response = await fetch(
      `${API_BASE}/api/v2/admin/models/${id}`,
      createFetchOptions()
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch model: ${response.statusText}`)
    }

    return response.json()
  },

  async createModel(data: any) {
    const response = await fetch(
      `${API_BASE}/api/v2/admin/models`,
      createFetchOptions({
        method: 'POST',
        body: JSON.stringify(data),
      })
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || `Failed to create model: ${response.statusText}`)
    }

    return response.json()
  },

  async updateModel(id: string, data: any) {
    const response = await fetch(
      `${API_BASE}/api/v2/admin/models/${id}`,
      createFetchOptions({
        method: 'PUT',
        body: JSON.stringify(data),
      })
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || `Failed to update model: ${response.statusText}`)
    }

    return response.json()
  },

  async deleteModel(id: string) {
    const response = await fetch(
      `${API_BASE}/api/v2/admin/models/${id}`,
      createFetchOptions({
        method: 'DELETE',
      })
    )

    if (!response.ok) {
      throw new Error(`Failed to delete model: ${response.statusText}`)
    }

    return response.json()
  },

  // Model Upload
  async startUpload(data: { filename: string; fileSize: number; mimeType?: string }) {
    const response = await fetch(
      `${API_BASE}/api/v2/admin/models/upload/start`,
      createFetchOptions({
        method: 'POST',
        body: JSON.stringify(data),
      })
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || `Failed to start upload: ${response.statusText}`)
    }

    return response.json()
  },

  async completeUpload(uploadId: string) {
    const response = await fetch(
      `${API_BASE}/api/v2/admin/models/upload/complete`,
      createFetchOptions({
        method: 'POST',
        body: JSON.stringify({ uploadId }),
      })
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || `Failed to complete upload: ${response.statusText}`)
    }

    return response.json()
  },

  // Permissions
  async getPermissions(modelId: string) {
    const response = await fetch(
      `${API_BASE}/api/v2/admin/models/${modelId}/permissions`,
      createFetchOptions()
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch permissions: ${response.statusText}`)
    }

    return response.json()
  },

  async addPermission(modelId: string, data: {
    userEmail: string
    permissionType: 'view' | 'download' | 'edit'
    expiresAt?: string
  }) {
    const response = await fetch(
      `${API_BASE}/api/v2/admin/models/${modelId}/permissions`,
      createFetchOptions({
        method: 'POST',
        body: JSON.stringify(data),
      })
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || `Failed to add permission: ${response.statusText}`)
    }

    return response.json()
  },

  async deletePermission(modelId: string, permissionId: number) {
    const response = await fetch(
      `${API_BASE}/api/v2/admin/models/${modelId}/permissions/${permissionId}`,
      createFetchOptions({
        method: 'DELETE',
      })
    )

    if (!response.ok) {
      throw new Error(`Failed to delete permission: ${response.statusText}`)
    }

    return response.json()
  },

  // Analytics
  async getAnalytics(modelId?: string, dateRange?: { start: string; end: string }) {
    const params = new URLSearchParams()
    if (modelId) params.append('modelId', modelId)
    if (dateRange?.start) params.append('start', dateRange.start)
    if (dateRange?.end) params.append('end', dateRange.end)

    const response = await fetch(
      `${API_BASE}/api/v2/admin/analytics?${params}`,
      createFetchOptions()
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch analytics: ${response.statusText}`)
    }

    return response.json()
  },

  // Categories
  async getCategories() {
    const response = await fetch(
      `${API_BASE}/api/v2/admin/categories`,
      createFetchOptions()
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch categories: ${response.statusText}`)
    }

    return response.json()
  },

  async createCategory(data: { name: string; slug: string; description?: string }) {
    const response = await fetch(
      `${API_BASE}/api/v2/admin/categories`,
      createFetchOptions({
        method: 'POST',
        body: JSON.stringify(data),
      })
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || `Failed to create category: ${response.statusText}`)
    }

    return response.json()
  },

  // Media management
  async getModelMedia(modelId: string) {
    const response = await fetch(
      `${API_BASE}/api/v2/admin/models/${modelId}/media`,
      createFetchOptions()
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch media: ${response.statusText}`)
    }

    return response.json()
  },

  async setPrimaryThumbnail(modelId: string, thumbnailPath: string) {
    const response = await fetch(
      `${API_BASE}/api/v2/admin/models/${modelId}/primary-thumbnail`,
      createFetchOptions({
        method: 'PUT',
        body: JSON.stringify({ thumbnailPath }),
      })
    )

    if (!response.ok) {
      throw new Error(`Failed to set primary thumbnail: ${response.statusText}`)
    }

    return response.json()
  },

  async deleteMedia(modelId: string, path: string) {
    const response = await fetch(
      `${API_BASE}/api/v2/admin/models/${modelId}/media/${path}`,
      createFetchOptions({
        method: 'DELETE',
      })
    )

    if (!response.ok) {
      throw new Error(`Failed to delete media: ${response.statusText}`)
    }

    return response.json()
  },
}